(ns frontend.view.modals.account-disable.view
  (:require
   [clojure.set :as set]
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.entity-type-names :as entity-type-names]
   [common.schema.entity-types :as entity-types]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [frontend.view.modals.account-disable.events :as events]
   [frontend.view.modals.components.processing-modal :as processing-modal]
   [potpuri.core :refer [dissoc-in fn->]]
   [ui.color :as c]
   [ui.components.modal.view :as modal]
   [ui.components.radix-ui.popover.view :as popover]
   [ui.components.radix-ui.radio-group.view :refer
    [radio-group-indicator
     radio-group-item
     radio-group-root]]
   [ui.editors.data-input :as data-input]
   [ui.editors.labelled-input.view :as labelled-input]))

(defonce modal-key :account-disable-modal)

(defn owner-for?
  [entity-type account-id]
  (let [owner-attribute (entity-types/type->owner-attribute entity-type)
        entities (->> (session/get-entities entity-type)
                      (remove :common/disabled?)
                      (cu/only-entity owner-attribute account-id))]
    entities))

(defn kpi-owner?
  [account-id]
  (let [kpi-reports (owner-for? :entity.type/kpi-report account-id)
        kpi-members (->> @session/all-kpi-members
                         (remove :common/disabled?)
                         (cu/only-entity :common/owner account-id))]
    (concat kpi-reports kpi-members)))

(defn member-in-contact-lists
  [account-id]
  (when (session/contact-lists?)
    (->> @session/contact-lists
         (filter (fn [{:keys [contact-list/members]}]
                   (some #{account-id} members))))))

(def pipeline-attribute-by-entity-type
  {:entity.type/oppo                    :oppo/pipeline
   :entity.type/fundraising-opportunity :fundraising-opportunity/pipeline
   :entity.type/sales-opportunity       :sales-opportunity/pipeline
   :entity.type/exit-opportunity        :exit-opportunity/pipeline})

(def module-by-entity-type
  {:entity.type/holding        :module/portfolio
   :entity.type/sale-agreement :module/sales-pipelines
   :entity.type/kpi-report     :module/kpi-reporting})

(defn account-ids
  [accounts]
  (->> accounts
       (map :db/id)
       (set)))

(defn module-access-account-ids
  [entity-type]
  (case entity-type
    :entity.type/fund-commitment
    (set/union (account-ids (session/active-accounts-with-module-access :module/fundraising))
               (account-ids (session/active-accounts-with-module-access :module/portfolio)))

    (when-let [module (get module-by-entity-type entity-type)]
      (account-ids (session/active-accounts-with-module-access module)))))

(defn pipeline-access-account-ids
  [accounts entity-type owned-entities]
  (let [pipeline-attribute (get pipeline-attribute-by-entity-type entity-type)
        pipeline-ids (when pipeline-attribute
                       (->> owned-entities
                            (keep pipeline-attribute)
                            (set)))]
    (if-not (seq pipeline-ids)
      (account-ids accounts)
      (->> accounts
           (filter (fn [candidate]
                     (every? (fn [pipeline-id]
                               (session/access-to-pipeline candidate pipeline-id))
                             pipeline-ids)))
           (account-ids)))))

(defn owner-candidate-accounts
  [account entity-type owned-entities]
  (let [account-id (:db/id account)
        base-accounts (->> (session/tenant-users)
                           (remove (fn [candidate]
                                     (= (:db/id candidate) account-id))))
        base-account-ids (account-ids base-accounts)
        pipeline-account-ids (pipeline-access-account-ids base-accounts
                                                          entity-type
                                                          owned-entities)
        module-account-ids (module-access-account-ids entity-type)
        pipeline-constrained-account-ids (set/intersection base-account-ids pipeline-account-ids)
        allowed-account-ids (if (some? module-account-ids)
                              (set/intersection pipeline-constrained-account-ids module-account-ids)
                              pipeline-constrained-account-ids)]
    (->> base-accounts
         (filter (fn [candidate]
                   (contains? allowed-account-ids (:db/id candidate))))
         (sort-by (fn-> entity/display-string str string/trim string/lower-case)))))

(defn select-owner-view
  [state account entity-type owned-entities]
  (let [entity-plural (loc/tr (keyword "disable-account-modal" (str "plural-" (name entity-type))))
        text (loc/tr :disable-account-modal/select-new-owner [entity-plural])
        accounts (owner-candidate-accounts account entity-type owned-entities)
        edit-kw (keyword (str (name entity-type) "-owner"))
        value (get-in @state [:edit edit-kw])
        attribute (entity-types/type->owner-attribute entity-type)
        options (->> accounts
                     (map (fn [c]
                            {:value (:db/id c)
                             :label (entity/display-string c)})))]
    [:div
     {:class "flex flex-col gap-2"}
     [:div
      {:class "m-0 font-bold text-md"}
      text]
     [labelled-input/view
      {:editor-type :editor.type/select
       :options     options
       :label       (let [fs (registry/get-field-schema @session/tenant-schema
                                                        attribute)]
                      (loc/field-name attribute fs))
       :value       (->> options
                         (cu/filter-by :value value)
                         (first))
       :on-change   (fn [new-value]
                      (events/update! state edit-kw new-value))}]]))

(defn select-contact-list-membership
  [state account contact-lists]
  (let [contact-list-count (count contact-lists)
        contact-list-handling-strategy (get-in @state [:edit :contact-list-handling-strategy])
        account-id (:db/id account)
        replace-with-account-options
        (->> (session/active-accounts nil)
             (keep (fn [account]
                     (let [id (:db/id account)]
                       (when-not (= id account-id)
                         {:key   id
                          :label (entity/display-string account)}))))
             (vec))]
    [:div
     {:class "p-3 flex flex-col gap-3 bg-base-regular rounded"}
     [:div
      {:class "flex gap-3 items-center"}
      [:span (str (loc/tr :this-account-is-a-member-in) " ")]

      [popover/view
       {:open?          (:popover-open? @state)
        :on-open-change (fn [open?]
                          (swap! state assoc :popover-open? open?))}
       [:b
        {:class "inline-block py-1 px-2 border border-solid border-base-light-border hover:border-base-light-border-accent rounded-full"}
        (str contact-list-count
             " "
             (if (< 1 contact-list-count)
               (-> :entity.type/contact-list
                   (entity-type-names/get-plural :en)
                   (string/lower-case))
               (loc/tr :entity.type/contact-list)))]
       [:div
        (->> contact-lists
             (map (fn [contact-list]
                    (let [id (:db/id contact-list)
                          label (entity/display-string contact-list)]
                      ^{:key (str "contact-list-" id)}
                      [:div label])))
             (doall))]]]

     [:span (str " " (loc/tr :remove-or-replace-account-in-contact-lists))]

     [:div
      [radio-group-root
       {:value           contact-list-handling-strategy
        :on-value-change (fn [value-str]
                           (let [value (keyword value-str)]
                             (swap! state assoc-in [:edit :contact-list-handling-strategy] value)))
        :class           "flex flex-col gap-3 "}
       [:div
        {:class "flex gap-3"}
        [radio-group-item
         {:value :remove
          :id    :remove-from-contact-lists
          :class "w-5 h-5 min-w-5 min-h-5 flex items-center justify-center rounded-full bg-base-light border border-solid border-base-light-border"}
         [radio-group-indicator
          {:class "flex items-center justify-center relative
                             after:content-[''] after:block after:w-4 after:h-4 after:rounded-full after:bg-secondary-regular after:absolute after:left-0 after:top-0 after:-translate-y-1/2 after:-translate-x-1/2"}]]

        [:div
         [:label
          {:for   :remove-from-contact-lists
           :class "font-semibold"}
          (loc/tr :remove)]
         [:p
          {:class "text-base-light-text-paler text-xs"}
          (loc/tr :remove-account-from-contact-lists-desc)]]]

       [:div
        {:class "flex gap-3"}
        [radio-group-item
         {:value :replace
          :id    :replace-in-contact-lists
          :class "w-5 h-5 min-w-5 min-h-5 flex items-center justify-center rounded-full bg-base-light border border-solid border-base-light-border"}
         [radio-group-indicator
          {:class "flex items-center justify-center relative
                             after:content-[''] after:block after:w-4 after:h-4 after:rounded-full after:bg-secondary-regular after:absolute after:left-0 after:top-0 after:-translate-y-1/2 after:-translate-x-1/2"}]]

        [:div
         [:label
          {:for   :replace-in-contact-lists
           :class "block pb-2 font-semibold"}
          (loc/tr :replace)]

         (when (= contact-list-handling-strategy :replace)
           [labelled-input/view
            {:editor-type :editor.type/select
             :options     replace-with-account-options
             :label       (loc/tr :replace-account-in-contact-lists-desc)
             :value       (get-in @state [:edit :contact-list-replacement-account])
             :on-change   (fn [_ value-str]
                            (let [value (js/parseInt value-str)]
                              (if (js/isNaN value)
                                (swap! state dissoc-in [:edit :contact-list-replacement-account])
                                (swap! state assoc-in [:edit :contact-list-replacement-account] (js/parseInt value)))))}])]]]]]))

(defn view-body
  [state params]
  (let [{:keys [account
                owner?
                owner-for-webforms
                owner-for-fund-commitments
                owner-for-fundraising-opportunities
                owner-for-sales-opportunities
                owner-for-sale-agreements
                owner-for-exit-opportunities
                owner-for-deals
                owner-for-companies
                owner-for-holdings
                owner-for-kpi
                owner-for-persons
                member-in-contact-lists]}
        params
        {:keys [team-confirmation? owner-assignment-confirmation?]} (:confirm @state)]
    [:div
     {:class "flex flex-col gap-4"}
     [:div
      {:class "flex flex-col gap-4"}
      [:div
       {:class "flex flex-col gap-4"}
       [:div
        {:class "p-3 flex items-baseline gap-3 bg-danger-light border border-solid border-danger-light-border rounded"}
        [:i.fas.fa-exclamation-triangle.text-danger-light-text-accent.text-xl]
        [:div
         {:class "flex flex-col gap-2"}
         [:p
          {:class "text-danger-light-text"}
          (loc/tr :do-you-wish-to-deactivate-account)]
         [:div
          {:class "text-danger-light-text font-bold text-lg"}
          [:strong (entity/display-string account)]]
         (when owner?
           [:div
            {:class "flex flex-col gap-2"}
            [:span
             {:class "text-danger-light-text text-xs"}
             (loc/tr :disable-account-modal/assignment-info)]])]]

       (when owner?
         [:div
          {:class "flex flex-col gap-2"}
          (when (seq owner-for-webforms)
            [select-owner-view state account :entity.type/webentry owner-for-webforms])

          (when (seq owner-for-deals)
            [select-owner-view state account :entity.type/oppo owner-for-deals])

          (when (seq owner-for-persons)
            [select-owner-view state account :entity.type/person owner-for-persons])

          (when (seq owner-for-companies)
            [select-owner-view state account :entity.type/company owner-for-companies])

          (when (seq owner-for-fund-commitments)
            [select-owner-view state account
             :entity.type/fund-commitment
             owner-for-fund-commitments])

          (when (seq owner-for-fundraising-opportunities)
            [select-owner-view state account
             :entity.type/fundraising-opportunity
             owner-for-fundraising-opportunities])

          (when (seq owner-for-sales-opportunities)
            [select-owner-view state account
             :entity.type/sales-opportunity
             owner-for-sales-opportunities])

          (when (seq owner-for-sale-agreements)
            [select-owner-view state account :entity.type/sale-agreement owner-for-sale-agreements])

          (when (seq owner-for-exit-opportunities)
            [select-owner-view state account
             :entity.type/exit-opportunity
             owner-for-exit-opportunities])

          (when (seq owner-for-holdings)
            [select-owner-view state account :entity.type/holding owner-for-holdings])

          (when (seq owner-for-kpi)
            [select-owner-view state account :entity.type/kpi-report owner-for-kpi])])]

      (when (seq member-in-contact-lists)
        [select-contact-list-membership state account member-in-contact-lists])

      [:div
       {:class "flex flex-col gap-4"}
       [:div
        {:class "flex items-baseline gap-2"}
        [:label
         {:class "font-semibold flex flex-row gap-2"}
         [data-input/view
          {:editor-type :editor.type/checkbox
           :value       (boolean team-confirmation?)
           :on-change   #(swap! state update-in [:confirm :team-confirmation?] not)}]
         (loc/tr :disable-account-modal/team-confirmation)]]
       (when owner?
         [:div
          {:class "flex items-baseline gap-2"}
          [:label
           {:class "font-semibold flex flex-row gap-2"}
           [data-input/view
            {:editor-type :editor.type/checkbox
             :value       (boolean owner-assignment-confirmation?)
             :on-change   #(swap! state update-in [:confirm :owner-assignment-confirmation?] not)}]
           (loc/tr :disable-account-modal/owner-assignment-confirmation)]])]]]))

(defn footer-view
  [{:keys [disabled? on-confirm! state on-close! account role
           on-submit!]}]
  [:<>
   [modal/left-cancel-only on-close! nil]
   [modal/right-single-button ::c/primary disabled?
    (fn []
      (when-not disabled?
        (if on-confirm!
          (on-confirm! (merge
                        (:edit @state)
                        {:on-close! on-close!}))
          (events/disable-account! account role state on-submit!)
          )))
    (loc/tr :confirm)
    nil]])

(defn view
  [{:keys [props close-modal! _shown? _state _class-name _topmost?]}]
  (let [{:keys [account role on-confirm! on-submit! on-close!]} props
        account-id (:db/id account)
        on-close! (fn []
                    (close-modal!)
                    (when on-close!
                      (on-close!)))
        ownerships {:owner-for-webforms            (owner-for? :entity.type/webentry account-id)
                    :owner-for-deals               (owner-for? :entity.type/oppo account-id)
                    :owner-for-persons             (owner-for? :entity.type/person account-id)
                    :owner-for-companies           (owner-for? :entity.type/company account-id)
                    :owner-for-fund-commitments    (owner-for? :entity.type/fund-commitment account-id)
                    :owner-for-fundraising-opportunities
                    (owner-for? :entity.type/fundraising-opportunity account-id)
                    :owner-for-sales-opportunities (owner-for? :entity.type/sales-opportunity
                                                               account-id)
                    :owner-for-sale-agreements     (owner-for? :entity.type/sale-agreement account-id)
                    :owner-for-exit-opportunities  (owner-for? :entity.type/exit-opportunity
                                                               account-id)
                    :owner-for-kpi                 (kpi-owner? account-id)
                    :owner-for-holdings            (owner-for? :entity.type/holding account-id)}
        member-in-contact-lists (member-in-contact-lists account-id)
        params (assoc ownerships
                 :owner? (or (seq (:owner-for-webforms ownerships))
                             (seq (:owner-for-deals ownerships))
                             (seq (:owner-for-companies ownerships))
                             (seq (:owner-for-persons ownerships))
                             (seq (:owner-for-fund-commitments ownerships))
                             (seq (:owner-for-fundraising-opportunities ownerships))
                             (seq (:owner-for-sales-opportunities ownerships))
                             (seq (:owner-for-sale-agreements ownerships))
                             (seq (:owner-for-exit-opportunities ownerships))
                             (seq (:owner-for-kpi ownerships))
                             (seq (:owner-for-holdings ownerships)))
                 :member-in-contact-lists member-in-contact-lists
                 :account account)]
    (fn [{:keys [shown? state class-name topmost?]}]
      (let [{:keys [webentry-owner
                    oppo-owner
                    person-owner
                    company-owner
                    fundraising-opportunity-owner
                    contact-list-handling-strategy
                    contact-list-replacement-account
                    fund-commitment-owner
                    sales-opportunity-owner
                    sale-agreement-owner
                    exit-opportunity-owner
                    kpi-report-owner
                    holding-owner]}
            (:edit @state)
            {:keys [team-confirmation? owner-assignment-confirmation?]} (:confirm @state)
            disabled?
            (or (and (seq (:owner-for-webforms params)) (not webentry-owner))
                (and (seq (:owner-for-deals params)) (not oppo-owner))
                (and (seq (:owner-for-persons params)) (not person-owner))
                (and (seq (:owner-for-companies params)) (not company-owner))
                (and (seq (:owner-for-fund-commitments params)) (not fund-commitment-owner))
                (and (seq (:owner-for-fundraising-opportunities params))
                     (not fundraising-opportunity-owner))
                (and (seq (:owner-for-holdings params)) (not holding-owner))
                (and (seq (:owner-for-sales-opportunities params)) (not sales-opportunity-owner))
                (and (seq (:owner-for-sale-agreements params)) (not sale-agreement-owner))
                (and (seq (:owner-for-exit-opportunities params))
                     (not exit-opportunity-owner))
                (and (seq (:owner-for-kpi params)) (not kpi-report-owner))
                (not team-confirmation?)
                (and (not owner-assignment-confirmation?) (:owner? params))
                (and (session/contact-lists?)
                     (seq member-in-contact-lists)
                     (or (not contact-list-handling-strategy)
                         (and (= contact-list-handling-strategy :replace)
                              (not contact-list-replacement-account)))))]
        [processing-modal/render-modal
         {:state             state
          :shown?            (and shown? topmost?)
          :success-text      (loc/tr :success-account-disabled)
          :on-close!         on-close!
          :class-name        class-name
          :default-title-fn  #(loc/tr :disable-account-modal/title)
          :default-body-fn   (partial view-body state params)
          :default-footer-fn (partial footer-view
                                      {:disabled?   disabled?
                                       :on-confirm! on-confirm!
                                       :state       state
                                       :on-close!   on-close!
                                       :account     account
                                       :role        role
                                       :on-submit!  on-submit!})}]))))
